import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Instagram, Music2, Youtube, ArrowUpRight, Lock } from "lucide-react";
import api, { resolveImg } from "@/lib/api";

const IMAGES = {
  brain: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/a704854c7863fb46bb074deff16064da464555a4e6e105945a175754d600ec10.png",
  eye: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/2ca5f8698c16257ffed6b65cbf761017c269aed3e0f73d9990f888c3c32f2ef8.png",
  serpent: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/ddf9e3adefcda9807ee11e69da605ec5ac1aa668ed189d64d883bf6c9762db93.png",
  angel: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/228a592e1bcedf59c7162affee25473b0b87c481f5e41ced915a269f030b95dd.png",
  starcross: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/cfbbd3b3b191e4f79b56a6958c9033936fb2e41c233d95c008ef9a7546f71092.png",
  syringe: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/ae5ca50aa859e0d8c9d44a8e5cfaa72d6ea88d5a74ca8708143af846a97e150d.png",
  pills: "https://static.prod-images.emergentagent.com/jobs/4a78a10a-2657-48f2-a998-4664cfa14f35/images/fbee96eb6d1f36fec5994ff5f3e1faf9d104debd77c37406119980e829ec28dd.png",
};

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.107zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/>
  </svg>
);

const SOCIAL_ICONS = { INSTAGRAM: Instagram, SPOTIFY: Music2, SOUNDCLOUD: Music2, YOUTUBE: Youtube };

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.06 } }),
};

export default function Landing() {
  const [site, setSite] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [shows, setShows] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/site"),
      api.get("/tracks"),
      api.get("/shows"),
      api.get("/gallery"),
    ]).then(([s, t, sh, g]) => {
      setSite(s.data);
      setTracks(t.data);
      setShows(sh.data);
      setGallery(g.data);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wa = (site?.whatsapp_number || "+41799422741").replace(/[^0-9]/g, "");
  const waLink = `https://wa.me/${wa}`;

  return (
    <div className="bg-white text-black font-mono selection:bg-black selection:text-white overflow-x-hidden">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-white border-b-4 border-black flex items-center justify-between px-5 md:px-10 h-16">
        <span className="font-scribble text-3xl md:text-4xl leading-none" data-testid="nav-logo">LilBrain jr</span>
        <div className="hidden md:flex items-center gap-7 text-xs uppercase tracking-widest">
          <a href="#music" className="hover:line-through" data-testid="nav-music">music</a>
          <a href="#shows" className="hover:line-through" data-testid="nav-shows">shows</a>
          <a href="#gallery" className="hover:line-through" data-testid="nav-gallery">gallery</a>
          <a href="#about" className="hover:line-through" data-testid="nav-about">gospel</a>
        </div>
        <Link to="/admin/login" data-testid="nav-admin-link" className="border-2 border-black px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-black hover:text-white transition-colors">
          <Lock size={13} strokeWidth={3} /> admin
        </Link>
      </nav>

      {/* HERO */}
      <header className="relative min-h-[88vh] flex flex-col justify-center px-5 md:px-10 py-16 border-b-4 border-black">
        <img src={IMAGES.eye} alt="" className="bw-img absolute top-6 right-4 w-28 md:w-52 opacity-90 rotate-6 pointer-events-none select-none" />
        <img src={IMAGES.serpent} alt="" className="bw-img absolute bottom-6 left-2 w-24 md:w-44 -rotate-6 pointer-events-none select-none hidden sm:block" />
        <motion.div initial="hidden" animate="show" className="relative z-10 max-w-6xl">
          <motion.p custom={0} variants={fadeUp} className="text-xs md:text-sm uppercase tracking-[0.3em] mb-4" data-testid="hero-tagline">
            {site?.hero_tagline || "saint of the syringe // child of the void"}
          </motion.p>
          <motion.h1 custom={1} variants={fadeUp} className="font-scribble text-7xl sm:text-8xl lg:text-[12rem] leading-[0.82] uppercase break-words" data-testid="hero-title">
            {site?.hero_title || "LILBRAIN JR"}
          </motion.h1>
          <motion.p custom={2} variants={fadeUp} className="mt-6 text-lg md:text-2xl uppercase tracking-tight font-bold" data-testid="hero-subtitle">
            {site?.hero_subtitle || "GOSPEL FROM THE GUTTER"}
          </motion.p>
          <motion.div custom={3} variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
            <a href="#music" data-testid="hero-listen-btn" className="bg-black text-white px-7 py-4 uppercase tracking-widest text-sm hard-shadow hover:bg-white hover:text-black border-2 border-black transition-colors flex items-center gap-3">
              <Play size={16} strokeWidth={3} fill="currentColor" /> listen now
            </a>
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="bg-white text-black px-7 py-4 uppercase tracking-widest text-sm border-2 border-black hard-shadow hover:bg-black hover:text-white transition-colors flex items-center gap-3">
              <WhatsAppIcon className="w-4 h-4" /> book / dm
            </a>
          </motion.div>
        </motion.div>
        <img src={IMAGES.brain} alt="lilbrain jr mascot" className="bw-img absolute -bottom-2 right-6 w-32 md:w-56 pointer-events-none select-none" />
      </header>

      {/* MARQUEE */}
      <div className="bg-black text-white py-4 border-b-4 border-black overflow-hidden">
        <div className="marquee">
          <div className="marquee__inner font-scribble text-4xl md:text-6xl uppercase">
            {Array.from({ length: 2 }).map((_, k) => (
              <span key={k} className="flex shrink-0">
                {["HOLY OVERDOSE","☩","NO GODS NO DEALERS","✝","CHILD OF THE VOID","☩","PRAY & PAY","✝"].map((w, i) => (
                  <span key={i} className="px-6 shrink-0">{w}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* MUSIC */}
      <section id="music" className="px-5 md:px-10 py-20 border-b-4 border-black">
        <SectionTitle index="01" title="MUSIC" />
        <div className="mt-10 border-4 border-black" data-testid="tracks-list">
          {tracks.map((t, i) => (
            <a key={t.id} href={t.link || "#"} target={t.link ? "_blank" : undefined} rel="noreferrer"
               data-testid={`track-row-${i}`}
               className={`group flex items-center gap-5 px-5 md:px-8 py-5 md:py-6 hover:bg-black hover:text-white transition-colors ${i !== tracks.length - 1 ? "border-b-4 border-black" : ""}`}>
              <span className="font-scribble text-3xl w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="w-11 h-11 shrink-0 border-2 border-black group-hover:border-white flex items-center justify-center">
                <Play size={16} strokeWidth={3} fill="currentColor" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-lg md:text-2xl font-bold uppercase truncate">{t.title}</p>
                {t.subtitle ? <p className="text-xs uppercase tracking-widest opacity-70 truncate">{t.subtitle}</p> : null}
              </div>
              <span className="text-sm tabular-nums">{t.duration}</span>
            </a>
          ))}
          {tracks.length === 0 && <p className="p-8 uppercase tracking-widest text-sm">no tracks yet.</p>}
        </div>
      </section>

      {/* ABOUT / GOSPEL */}
      <section id="about" className="bg-black text-white px-5 md:px-10 py-20 border-b-4 border-black">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] mb-4 opacity-70">02 — gospel</p>
            <h2 className="font-scribble text-5xl md:text-7xl uppercase leading-none">the gospel</h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed uppercase" data-testid="about-bio">
              {site?.bio || "born somewhere between a sunday school and a back alley."}
            </p>
          </div>
          <div className="md:col-span-5 flex justify-center">
            <img src={IMAGES.starcross} alt="star of david with cross" className="bw-img w-64 md:w-80 bg-white border-4 border-white hard-shadow-white invert" />
          </div>
        </div>
      </section>

      {/* SHOWS */}
      <section id="shows" className="px-5 md:px-10 py-20 border-b-4 border-black">
        <SectionTitle index="03" title="SHOWS" />
        <div className="mt-10 border-t-4 border-black" data-testid="shows-list">
          {shows.map((s, i) => (
            <div key={s.id} data-testid={`show-row-${i}`} className="grid grid-cols-12 items-center gap-3 py-6 border-b-4 border-black group hover:bg-black hover:text-white transition-colors px-2">
              <span className="col-span-12 sm:col-span-3 font-scribble text-3xl">{formatDate(s.date)}</span>
              <span className="col-span-6 sm:col-span-3 text-lg font-bold uppercase">{s.city}</span>
              <span className="col-span-6 sm:col-span-4 text-sm uppercase tracking-widest opacity-80">{s.venue}</span>
              <div className="col-span-12 sm:col-span-2 sm:text-right">
                {s.sold_out ? (
                  <span className="inline-block border-2 border-current px-3 py-1 text-xs uppercase tracking-widest line-through">sold out</span>
                ) : (
                  <a href={s.ticket_link || "#"} target={s.ticket_link ? "_blank" : undefined} rel="noreferrer" className="inline-flex items-center gap-1 border-2 border-current px-3 py-1 text-xs uppercase tracking-widest hover:bg-white hover:text-black group-hover:bg-white group-hover:text-black">
                    tickets <ArrowUpRight size={13} strokeWidth={3} />
                  </a>
                )}
              </div>
            </div>
          ))}
          {shows.length === 0 && <p className="py-8 uppercase tracking-widest text-sm">no shows announced.</p>}
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="px-5 md:px-10 py-20 border-b-4 border-black">
        <SectionTitle index="04" title="GALLERY" />
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="gallery-grid">
          {gallery.map((g, i) => (
            <figure key={g.id} data-testid={`gallery-item-${i}`} className={`border-4 border-black hard-shadow bg-white ${i % 3 === 0 ? "md:row-span-2" : ""}`}>
              <div className="aspect-square overflow-hidden border-b-4 border-black bg-white">
                <img src={resolveImg(g.image_url)} alt={g.caption} className="bw-img w-full h-full object-cover" />
              </div>
              <figcaption className="px-3 py-2 text-xs uppercase tracking-widest font-bold truncate">{g.caption || "untitled"}</figcaption>
            </figure>
          ))}
          {gallery.length === 0 && <p className="py-8 uppercase tracking-widest text-sm">gallery empty.</p>}
        </div>
      </section>

      {/* CONTACT / SOCIALS */}
      <section id="contact" className="bg-black text-white px-5 md:px-10 py-24">
        <p className="text-xs uppercase tracking-[0.3em] mb-4 opacity-70">05 — find the cult</p>
        <h2 className="font-scribble text-6xl md:text-8xl uppercase leading-none">stay loaded</h2>
        <div className="mt-10 flex flex-wrap gap-4" data-testid="socials-list">
          {(site?.socials || []).map((s, i) => {
            const Icon = SOCIAL_ICONS[s.label?.toUpperCase()] || ArrowUpRight;
            return (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`social-${i}`}
                 className="flex items-center gap-3 border-2 border-white px-5 py-4 uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors">
                <Icon size={16} strokeWidth={2.5} /> {s.label}
              </a>
            );
          })}
        </div>
        <a href={waLink} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
           className="mt-10 inline-flex items-center gap-3 bg-white text-black px-8 py-5 uppercase tracking-widest text-base hard-shadow-white hover:bg-black hover:text-white border-2 border-white transition-colors">
          <WhatsAppIcon className="w-5 h-5" /> message on whatsapp
        </a>
        <p className="mt-16 text-xs uppercase tracking-widest opacity-50 font-scribble text-2xl">© LilBrain jr — all sins reserved</p>
      </section>

      {/* FLOATING WHATSAPP */}
      <a href={waLink} target="_blank" rel="noreferrer" data-testid="floating-whatsapp-btn"
         className="fixed bottom-5 right-5 z-50 bg-black text-white border-2 border-black hard-shadow flex items-center gap-2 px-5 py-4 uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors">
        <WhatsAppIcon className="w-5 h-5" /> <span className="hidden sm:inline">whatsapp</span>
      </a>
    </div>
  );
}

function SectionTitle({ index, title }) {
  return (
    <div className="flex items-end gap-4">
      <span className="text-xs uppercase tracking-[0.3em] mb-3">{index}</span>
      <h2 className="font-scribble text-5xl md:text-7xl uppercase leading-none">{title}</h2>
    </div>
  );
}

function formatDate(d) {
  try {
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase();
  } catch { return d; }
}
